class ProductDownloads extends HTMLElement {
  constructor() {
    super();
    this.downloadsData = [];
    this.filteredData = [];
    this.currentFilter = 'all';
    this.searchQuery = '';
    this.currentPage = 1;
    this.itemsPerPage = 10;
  }

  connectedCallback() {
    this.endpoint = this.dataset.endpoint;
    this.customerId = this.dataset.customerId;
    
    // Read B2B status from data attribute (read here to ensure it's fresh)
    this.isB2B = this.dataset.isB2b === 'true';

    this.setupEventListeners();
    this.fetchDownloads();
  }

  setupEventListeners() {
    const searchInput = this.querySelector('[data-search-input]');
    const searchButton = this.querySelector('[data-search-button]');
    const filters = this.querySelectorAll('[data-filter]');

    if (searchInput) {
      searchInput.addEventListener('keypress', (e) => {
        if (e.key === 'Enter') {
          this.handleSearch();
        }
      });
    }

    if (searchButton) {
      searchButton.addEventListener('click', () => this.handleSearch());
    }

    filters.forEach(filter => {
      filter.addEventListener('click', () => {
        filters.forEach(f => f.setAttribute('data-active', 'false'));
        filter.setAttribute('data-active', 'true');
        this.currentFilter = filter.dataset.filter;
        this.applyFilters();
      });
    });

    // Add pagination event listeners
    this.addPaginationListeners();
  }

  addPaginationListeners() {
    const prevButton = this.querySelector('[data-pagination-prev]');
    const nextButton = this.querySelector('[data-pagination-next]');

    if (prevButton) {
      prevButton.addEventListener('click', () => {
        if (this.currentPage > 1) {
          this.currentPage--;
          this.renderResults();
        }
      });
    }

    if (nextButton) {
      nextButton.addEventListener('click', () => {
        const totalPages = Math.ceil(this.filteredData.length / this.itemsPerPage);
        if (this.currentPage < totalPages) {
          this.currentPage++;
          this.renderResults();
        }
      });
    }
  }

  async fetchDownloads(params = {}) {
    const loadingEl = this.querySelector('[data-loading]');
    const contentEl = this.querySelector('[data-content]');
    const errorEl = this.querySelector('[data-error]');

    if (loadingEl) loadingEl.hidden = false;
    if (contentEl) contentEl.hidden = true;
    if (errorEl) errorEl.hidden = true;

    try {
      const allItems = [];
      let nextCursor = params.next || null;
      let usedFirstPagePrevious = false;

      const MAX_PAGES = 50;
      let pagesFetched = 0;

      do {
        const url = new URL(this.endpoint, window.location.origin);

        url.searchParams.set('pageSize', '250');

        if (params.search) url.searchParams.set('search', params.search);
        if (params.product) url.searchParams.set('product', params.product);
        if (params.variant) url.searchParams.set('variant', params.variant);
        if (params.sku) url.searchParams.set('sku', params.sku);

        if (nextCursor) {
          url.searchParams.set('next', nextCursor);
        } else if (!usedFirstPagePrevious && params.previous) {
          url.searchParams.set('previous', params.previous);
          usedFirstPagePrevious = true;
        }

        const response = await fetch(url.href, {
          method: 'GET',
          credentials: 'same-origin',
          headers: { 'Accept': 'application/json' }
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        const pageItems = data.items || data.pdfs || data.downloads || data.data || (Array.isArray(data) ? data : []);
        allItems.push(...pageItems);

        nextCursor = this.extractNextCursor(data);
        pagesFetched += 1;

        if (pagesFetched >= MAX_PAGES) {
          console.warn(`[Product Downloads] Reached safety cap of ${MAX_PAGES} pages; stopping.`);
          break;
        }
      } while (nextCursor);

      this.downloadsData = this.filterByB2B(allItems);

      this.applyFilters();

      if (loadingEl) loadingEl.hidden = true;
      if (contentEl) contentEl.hidden = false;
    } catch (error) {
      console.error('[Product Downloads] Fetch failed:', error);
      if (loadingEl) loadingEl.hidden = true;
      if (errorEl) errorEl.hidden = false;
    }
  }

  extractNextCursor(data) {
    return data && data.pageInfo && data.pageInfo.next ? data.pageInfo.next : null;
  }

  filterByB2B(items) {
    // Drawings column visibility is controlled by data-is-b2b (b2b or B2B customer tag) in Liquid
    return items;
  }

  handleSearch() {
    const searchInput = this.querySelector('[data-search-input]');
    this.searchQuery = searchInput?.value.trim() || '';
    this.currentPage = 1; // Reset to first page on search
    this.fetchDownloads({ search: this.searchQuery });
  }

  applyFilters() {
    let filtered = [...this.downloadsData];

    // Apply type filter
    if (this.currentFilter !== 'all') {
      const typeMap = {
        'om-guide': ['o&m guide', 'om guide'],
        'mod-plan': ['mod plan'],
        'drawings': ['drawings']
      };

      const allowedTypes = typeMap[this.currentFilter] || [];

      filtered = filtered.filter(item => {
        const type = (item.type || item.pdf_type || '').toLowerCase();
        const matches = allowedTypes.includes(type);
        return matches;
      });
    }

    this.filteredData = filtered;
    this.renderResults();
  }

  renderResults() {
    const resultsEl = this.querySelector('[data-results]');
    if (!resultsEl) {
      console.error('[Product Downloads] No results element found!');
      return;
    }

    if (this.filteredData.length === 0) {
      resultsEl.innerHTML = '<div class="product-downloads__empty">No downloads found.</div>';
      return;
    }

    // Calculate pagination
    const totalItems = this.filteredData.length;
    const totalPages = Math.ceil(totalItems / this.itemsPerPage);
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const currentPageData = this.filteredData.slice(startIndex, endIndex);

    // Create table structure using direct data structure
    // Only show Drawings column for B2B customers
    const showDrawingsColumn = this.isB2B;

    const tableHTML = `
      <div class="product-downloads__table-wrapper">
        <table class="product-downloads__table">
          <thead>
            <tr>
              <th class="product-downloads__table-header">Code</th>
              <th class="product-downloads__table-header product-downloads__table-header--description">Description</th>
              <th class="product-downloads__table-header">O&M Guide</th>
              <th class="product-downloads__table-header">Installation Guide</th>
              <th class="product-downloads__table-header">Mod Plan</th>
              ${showDrawingsColumn ? '<th class="product-downloads__table-header">Drawings</th>' : ''}
            </tr>
          </thead>
          <tbody>
            ${currentPageData.map(product => {
              return `
              <tr class="product-downloads__table-row">
                <td class="product-downloads__table-cell product-downloads__table-cell--code">${product.code || 'N/A'}</td>
                <td class="product-downloads__table-cell product-downloads__table-cell--description">${product.productUrl ? `<a href="${product.productUrl}" class="product-downloads__product-link">${product.title || 'No description'}</a>` : (product.title || 'No description')}</td>
                <td class="product-downloads__table-cell product-downloads__table-cell--download">
                  ${product.omUrl ?
                    `<a href="${product.omUrl}" class="product-downloads__download-link product-downloads__download-link--pdf" download>PDF</a>` :
                    '<span class="product-downloads__no-download">-</span>'}
                </td>
                <td class="product-downloads__table-cell product-downloads__table-cell--download">
                  ${product.installationGuideUrl ?
                    `<a href="${product.installationGuideUrl}" class="product-downloads__download-link product-downloads__download-link--pdf" download>PDF</a>` :
                    '<span class="product-downloads__no-download">-</span>'}
                </td>
                <td class="product-downloads__table-cell product-downloads__table-cell--download">
                  ${product.modPlanUrl ?
                    `<a href="${product.modPlanUrl}" class="product-downloads__download-link product-downloads__download-link--pdf" download>PDF</a>` :
                    '<span class="product-downloads__no-download">-</span>'}
                </td>
                ${showDrawingsColumn ? `
                <td class="product-downloads__table-cell product-downloads__table-cell--download">
                  ${(product.drawingsDwgUrl || product.drawingsPdfUrl) ?
                    `<div class="product-downloads__drawings-links">
                      ${product.drawingsDwgUrl ? `<a href="${product.drawingsDwgUrl}" class="product-downloads__download-link product-downloads__download-link--dwg" download>DWG</a>` : ''}
                      ${product.drawingsPdfUrl ? `<a href="${product.drawingsPdfUrl}" class="product-downloads__download-link product-downloads__download-link--pdf" download>PDF</a>` : ''}
                    </div>` :
                    '<span class="product-downloads__no-download">-</span>'}
                </td>` : ''}
              </tr>
              `;
            }).join('')}
          </tbody>
        </table>
      </div>
    `;

    // Create pagination controls if needed
    let paginationHTML = '';
    if (totalPages > 1) {
      paginationHTML = `
        <div class="product-downloads__pagination">
          <button class="product-downloads__pagination-button product-downloads__pagination-button--prev"
                  ${this.currentPage === 1 ? 'disabled' : ''}
                  data-pagination-prev>
            Previous
          </button>
          <span class="product-downloads__pagination-info">
            Page ${this.currentPage} of ${totalPages}
          </span>
          <button class="product-downloads__pagination-button product-downloads__pagination-button--next"
                  ${this.currentPage === totalPages ? 'disabled' : ''}
                  data-pagination-next>
            Next
          </button>
        </div>
      `;
    }

    resultsEl.innerHTML = tableHTML + paginationHTML;

    // Re-add pagination listeners since HTML was regenerated
    this.addPaginationListeners();
  }

  getTypeClass(type) {
    const normalized = type.toLowerCase();
    if (normalized.includes('o&m') || normalized.includes('om guide')) {
      return 'product-downloads__type-badge--om-guide';
    }
    if (normalized.includes('mod plan')) {
      return 'product-downloads__type-badge--mod-plan';
    }
    if (normalized.includes('drawing')) {
      return 'product-downloads__type-badge--drawings';
    }
    return '';
  }

  getTypeLabel(type) {
    const normalized = type.toLowerCase();
    if (normalized.includes('o&m') || normalized.includes('om guide')) {
      return 'O&M Guide';
    }
    if (normalized.includes('mod plan')) {
      return 'Mod Plan';
    }
    if (normalized.includes('drawing')) {
      return 'Drawings';
    }
    return type || 'PDF';
  }

  getFileTypeIcon(url) {
    if (!url || url === '#') return 'FILE';

    // Handle URLs with query parameters or fragments
    const cleanUrl = url.split(/[?#]/)[0];
    const match = cleanUrl.toLowerCase().match(/\.([a-z0-9]+)$/);

    if (match) {
      const ext = match[1];
      const types = {
        // Documents
        'pdf': 'PDF', 'doc': 'DOC', 'docx': 'DOCX', 'rtf': 'RTF', 'txt': 'TXT',
        'odt': 'ODT', 'ods': 'ODS', 'odp': 'ODP', 'pages': 'PAGES', 'numbers': 'NUMBERS',
        'key': 'KEYNOTE',

        // Spreadsheets
        'xls': 'XLS', 'xlsx': 'XLSX', 'csv': 'CSV', 'tsv': 'TSV',

        // Presentations
        'ppt': 'PPT', 'pptx': 'PPTX', 'pps': 'PPS', 'ppsx': 'PPSX',

        // Images
        'jpg': 'JPG', 'jpeg': 'JPG', 'png': 'PNG', 'gif': 'GIF', 'bmp': 'BMP',
        'tiff': 'TIFF', 'tif': 'TIF', 'webp': 'WEBP', 'svg': 'SVG', 'ico': 'ICO',
        'heic': 'HEIC', 'heif': 'HEIF', 'raw': 'RAW', 'cr2': 'CR2', 'nef': 'NEF',

        // Video
        'mp4': 'MP4', 'avi': 'AVI', 'mov': 'MOV', 'wmv': 'WMV', 'flv': 'FLV',
        'mkv': 'MKV', 'webm': 'WEBM', 'm4v': 'M4V', '3gp': '3GP', 'mpg': 'MPG',
        'mpeg': 'MPEG',

        // Audio
        'mp3': 'MP3', 'wav': 'WAV', 'flac': 'FLAC', 'aac': 'AAC', 'ogg': 'OGG',
        'wma': 'WMA', 'm4a': 'M4A', 'aiff': 'AIFF',

        // Archives
        'zip': 'ZIP', 'rar': 'RAR', '7z': '7Z', 'tar': 'TAR', 'gz': 'GZ',
        'bz2': 'BZ2', 'xz': 'XZ', 'dmg': 'DMG', 'iso': 'ISO',

        // Executables
        'exe': 'EXE', 'msi': 'MSI', 'app': 'APP', 'deb': 'DEB', 'rpm': 'RPM',
        'apk': 'APK',

        // Web
        'html': 'HTML', 'htm': 'HTML', 'css': 'CSS', 'js': 'JS', 'json': 'JSON',
        'xml': 'XML', 'php': 'PHP', 'asp': 'ASP', 'jsp': 'JSP',

        // Other common types
        'dwg': 'DWG', 'dxf': 'DXF', 'stl': 'STL', 'obj': 'OBJ', 'fbx': 'FBX',
        'psd': 'PSD', 'ai': 'AI', 'eps': 'EPS', 'indd': 'INDD', 'xd': 'XD'
      };

      return types[ext] || ext.toUpperCase();
    }

    return 'FILE';
  }
}

if (!customElements.get('product-downloads')) {
  customElements.define('product-downloads', ProductDownloads);
}